/**
 * Shared design tokens for the Khaime SDK components.
 *
 * Two token families, each distributed as a CSS block body that
 * consumers interpolate into their own scope selector (e.g. `:host`,
 * `.ks-drawer`, `#khaime-checkout-modal`):
 *
 *   OVERLAY_TOKENS  — Opinionated tokens for self-contained overlays
 *                     (modals, drawers, side panels, floating buttons).
 *                     Light surface, dark ink, consistent across hosts.
 *                     Use when the component floats above the page and
 *                     benefits from predictable contrast.
 *
 *   ADAPTIVE_TOKENS — currentColor-derived surfaces for inline
 *                     components (cart page, storefront grid, product
 *                     cards). Blends with whatever host text color is
 *                     inherited. Use when the component lives in the
 *                     page content flow.
 *
 * Both families honor (in priority order):
 *   1. Merchant dashboard tokens (--ks-*) — applied inline on shadow
 *      host by the SDK when the merchant has explicit config.
 *   2. Plugin-side host bridge (--ks-host-*) — scoped per container.
 *   3. WP theme.json vars (--wp--preset--*) — set at document root by
 *      any FSE theme; naturally inherits into shadow DOM and reaches
 *      document.body-level overlays.
 *   4. Hardcoded sensible default.
 *
 * Consumers can locally override specific tokens by redeclaring them
 * after the shared block.
 */

/**
 * Opinionated tokens for overlay surfaces.
 * Prefix: --kso-*  (khaime SDK overlay)
 */
export const OVERLAY_TOKENS = `
  --kso-accent: var(--ks-primary, var(--ks-host-primary, var(--wp--preset--color--primary, #6366f1)));
  --kso-accent-ink: var(--ks-primary-contrast, #ffffff);

  --kso-surface: #ffffff;
  --kso-surface-alt: #fafaf7;
  --kso-ink: #111827;
  --kso-ink-soft: #374151;
  --kso-muted: #6b7280;
  --kso-muted-2: #9ca3af;
  --kso-hairline: #e8e4d9;
  --kso-hairline-strong: #d1d5db;

  --kso-success: #0a8a65;
  --kso-success-bg: #ebf7f1;
  --kso-danger: #c54b3a;
  --kso-danger-ink: #8a2b1f;
  --kso-danger-bg: #fdf3f1;
  --kso-danger-border: #f5c9c0;

  --kso-radius-md: 14px;
  --kso-radius-sm: 10px;
  --kso-radius-xs: 8px;

  /* Unified button radius — same across every primary/secondary CTA
     so the plugin doesn't drift between sharp (2px) and round (10px)
     between cart page / drawer / modals. */
  --kso-btn-radius: 8px;

  --kso-font:
    var(--ks-host-font-body,
      var(--wp--preset--font-family--body,
        -apple-system, BlinkMacSystemFont, 'Helvetica Neue', 'Segoe UI', Roboto, sans-serif));
  --kso-font-display:
    var(--ks-host-font-display,
      var(--wp--preset--font-family--heading, var(--kso-font)));

  --kso-shadow:
    0 1px 2px rgba(17, 24, 39, 0.04),
    0 25px 60px -12px rgba(17, 24, 39, 0.22);
`;

/**
 * Adaptive tokens for inline surfaces.
 * Prefix: --ksp-*  (khaime SDK page)
 */
export const ADAPTIVE_TOKENS = `
  --ksp-accent: var(--ks-primary, var(--ks-host-primary, var(--wp--preset--color--primary, #b08a4a)));
  --ksp-accent-ink: var(--ks-primary-contrast, #ffffff);

  --ksp-font-body:
    var(--ks-host-font-body,
      var(--wp--preset--font-family--body,
        'Manrope', ui-sans-serif, -apple-system, BlinkMacSystemFont, 'Helvetica Neue', sans-serif));
  --ksp-font-display:
    var(--ks-host-font-display,
      var(--wp--preset--font-family--heading,
        'Fraunces', 'Cambria', 'Georgia', 'Times New Roman', serif));

  --ksp-radius-sm: var(--ks-host-radius-sm, 2px);
  --ksp-radius-md: var(--ks-host-radius-md, 4px);
  --ksp-radius-lg: var(--ks-host-radius-lg, 12px);

  /* Unified button radius — see --kso-btn-radius. */
  --ksp-btn-radius: 8px;

  --ksp-card-bg: color-mix(in srgb, currentColor 4%, transparent);
  --ksp-card-bg-strong: color-mix(in srgb, currentColor 7%, transparent);
  --ksp-hairline: color-mix(in srgb, currentColor 14%, transparent);
  --ksp-hairline-strong: color-mix(in srgb, currentColor 22%, transparent);
  --ksp-muted: color-mix(in srgb, currentColor 65%, transparent);
  --ksp-muted-2: color-mix(in srgb, currentColor 45%, transparent);
  --ksp-placeholder: color-mix(in srgb, currentColor 35%, transparent);

  --ksp-shadow:
    0 1px 2px color-mix(in srgb, currentColor 5%, transparent),
    0 18px 40px -20px color-mix(in srgb, currentColor 12%, transparent);

  --ksp-danger: #c54b3a;

  /* Primary CTA — opinionated dark-on-light so cart/continue/empty CTAs
     match the drawer + checkout modal language. Hover morphs to accent. */
  --ksp-cta-bg: #1a1a1a;
  --ksp-cta-ink: #ffffff;
`;
